import { useState } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { z } from 'zod';
import { MaskedInput } from '@/components/form/masked-input';
import { Field } from '@/components/form/field';
import { FormError } from '@/components/form/form-error';
import { useZodForm } from '@/components/form/use-zod-form';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ApiError } from '@/lib/api-error';
import { verifyRecoveryCode, verifyTwoFactor } from '../api/auth';
import { AuthCard } from '../components/auth-card';
import { TrustDeviceField } from '../components/trust-device-field';
import { totpCode } from '../schemas';
import { useAuth } from '../use-auth';

interface ChallengeState {
  challengeToken?: string;
  trustDeviceDays?: number;
  from?: string;
}

const schema = z
  .object({ recovery: z.boolean(), code: z.string().trim(), trustDevice: z.boolean() })
  .superRefine((value, context) => {
    if (value.code === '') {
      context.addIssue({ code: 'custom', path: ['code'], message: 'Digite o código.' });
    } else if (!value.recovery) {
      const totp = totpCode.safeParse(value.code);
      if (!totp.success) {
        context.addIssue({
          code: 'custom',
          path: ['code'],
          message: totp.error.issues[0]?.message,
        });
      }
    }
  });

/** Segunda etapa do login: código do aplicativo ou, na falta dele, código de recuperação. */
export function TwoFactorPage() {
  const navigate = useNavigate();
  const { applySession } = useAuth();
  const state = (useLocation().state ?? {}) as ChallengeState;
  const form = useZodForm(schema, { recovery: false, code: '', trustDevice: false });
  const [serverError, setServerError] = useState<unknown>(null);
  const recovery = form.watch('recovery');

  // Sem desafio (recarregou a página, entrou pela URL): a etapa recomeça pela senha.
  if (!state.challengeToken) return <Navigate to="/entrar" replace />;
  const challengeToken = state.challengeToken;

  const onSubmit = form.handleSubmit(async ({ recovery: byRecovery, code, trustDevice }) => {
    setServerError(null);
    try {
      const payload = byRecovery
        ? await verifyRecoveryCode(challengeToken, code)
        : await verifyTwoFactor(challengeToken, code, trustDevice);
      applySession(payload);
      navigate(state.from ?? '/', { replace: true });
    } catch (caught) {
      if (caught instanceof ApiError && caught.code === 'CHALLENGE_EXPIRED') {
        navigate('/entrar', { replace: true });
        return;
      }
      setServerError(caught);
    }
  });

  return (
    <AuthCard
      title="Verificação em duas etapas"
      description={
        recovery
          ? 'Digite um dos códigos de recuperação que você guardou.'
          : 'Digite o código de 6 dígitos do seu aplicativo autenticador.'
      }
    >
      <form onSubmit={onSubmit} className="space-y-4" noValidate>
        <FormError error={serverError} fallback="Não foi possível verificar o código." />
        <Field
          control={form.control}
          name="code"
          label={recovery ? 'Código de recuperação' : 'Código'}
          required
        >
          {(field) =>
            // Código de recuperação tem letra: campo de texto; o do aplicativo, a máscara de código.
            recovery ? (
              <Input {...field} autoFocus autoComplete="one-time-code" />
            ) : (
              <MaskedInput {...field} mask="code" autoFocus autoComplete="one-time-code" />
            )
          }
        </Field>
        {/* Código de recuperação não oferece a opção: quem perdeu o celular reconfigura o 2FA. */}
        {recovery ? null : (
          <TrustDeviceField
            days={state.trustDeviceDays}
            checked={form.watch('trustDevice')}
            onCheckedChange={(checked) => form.setValue('trustDevice', checked)}
          />
        )}
        <Button type="submit" className="w-full" disabled={form.formState.isSubmitting}>
          {form.formState.isSubmitting ? 'Verificando…' : 'Entrar'}
        </Button>
        <Button
          type="button"
          variant="ghost"
          className="w-full"
          onClick={() => {
            form.reset({ recovery: !recovery, code: '', trustDevice: false });
            setServerError(null);
          }}
        >
          {recovery ? 'Usar o aplicativo autenticador' : 'Perdi o acesso ao aplicativo'}
        </Button>
      </form>
    </AuthCard>
  );
}
